/**
 * HomeV5 — Aurora / Gradient Sky Theme
 * Dark: deep space navy/purple + floating orbs
 * Light: soft sky/lavender + pastel floating orbs
 */
import { Link } from '@inertiajs/react';
import { useHomeContent } from '@/lib/homeContent';
import { ImageCard, VideoCard, ChatDemo, VoiceDemo, HomeQuickActions, HomeImageTrends, HomeVideoTrends } from './HomeSections';
import { useIsDark } from '@/hooks/useIsDark';

const GRADIENTS_DARK = ['from-blue-600/20 to-violet-600/10', 'from-violet-600/20 to-pink-600/10', 'from-orange-600/20 to-amber-600/10', 'from-emerald-600/20 to-teal-600/10', 'from-pink-600/20 to-rose-600/10', 'from-cyan-600/20 to-blue-600/10'];
const GRADIENTS_LIGHT = ['from-blue-100 to-violet-100', 'from-violet-100 to-pink-100', 'from-orange-100 to-amber-100', 'from-emerald-100 to-teal-100', 'from-pink-100 to-rose-100', 'from-cyan-100 to-blue-100'];

const isVideoUrl = (url) => /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(url || '');

export default function HomeV5({ page, openRegister }) {
    const c = useHomeContent(page?.content);
    const dark = useIsDark();

    const bg = {
        root: dark ? 'linear-gradient(180deg,#020818 0%,#080c1a 30%,#0d0a18 60%,#100810 100%)' : 'linear-gradient(180deg,#eef2ff 0%,#f5f3ff 40%,#fdf4ff 70%,#fff5f0 100%)',
        card: dark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.75)',
        border: dark ? 'rgba(255,255,255,0.08)' : 'rgba(99,102,241,0.2)',
        glass: dark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.6)',
    };
    const text = dark ? 'text-white' : 'text-slate-900';
    const muted = dark ? 'text-white/50' : 'text-slate-600';
    const orb1 = dark ? 'rgba(59,130,246,0.15)' : 'rgba(99,102,241,0.12)';
    const orb2 = dark ? 'rgba(139,92,246,0.12)' : 'rgba(167,139,250,0.12)';
    const orb3 = dark ? 'rgba(255,123,0,0.12)' : 'rgba(251,146,60,0.1)';
    const grad = dark ? 'linear-gradient(135deg,#3b82f6,#8b5cf6,#ff7b00)' : 'linear-gradient(135deg,#6366f1,#8b5cf6,#f97316)';

    return (
        <div className="min-h-screen overflow-x-hidden" style={{ background: bg.root }}>
            {c.custom_css && <style dangerouslySetInnerHTML={{ __html: c.custom_css }} />}
            {/* Floating orbs */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute w-[600px] h-[600px] rounded-full" style={{ background: `radial-gradient(circle,${orb1},transparent)`, filter: 'blur(100px)', top: '-10%', left: '-5%', animation: 'float1 8s ease-in-out infinite' }} />
                <div className="absolute w-[500px] h-[500px] rounded-full" style={{ background: `radial-gradient(circle,${orb2},transparent)`, filter: 'blur(120px)', top: '20%', right: '-5%', animation: 'float2 10s ease-in-out infinite' }} />
                <div className="absolute w-[400px] h-[400px] rounded-full" style={{ background: `radial-gradient(circle,${orb3},transparent)`, filter: 'blur(100px)', bottom: '10%', left: '40%', animation: 'float3 12s ease-in-out infinite' }} />
            </div>
            <style>{`@keyframes float1{0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(30px,20px) scale(1.1)}} @keyframes float2{0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-20px,30px) scale(1.05)}} @keyframes float3{0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(10px,-20px) scale(1.08)}}`}</style>

            {/* ── Hero ─────────────────────────────────────────── */}
            <section className="relative min-h-[90vh] flex items-center">
                {c.hero_bg && (
                    isVideoUrl(c.hero_bg) ? (
                        <video key={c.hero_bg} className="absolute inset-0 h-full w-full object-cover mix-blend-luminosity" autoPlay loop muted playsInline style={{ opacity: 0.1 }}>
                            <source src={c.hero_bg} type="video/mp4" />
                        </video>
                    ) : (
                        <div className="absolute inset-0 bg-cover bg-center opacity-10 mix-blend-luminosity" style={{ backgroundImage: `url(${c.hero_bg})` }} />
                    )
                )}
                <div className="relative z-10 max-w-[1200px] mx-auto px-6 py-24 text-center">
                    <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full border mb-10"
                        style={{ background: bg.glass, backdropFilter: 'blur(20px)', borderColor: bg.border }}>
                        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                        <span className={`text-sm font-medium ${muted}`}>{c.hero_small_title}</span>
                    </div>
                    <h1 className={`text-7xl md:text-9xl font-black leading-[1.0] tracking-tight ${text} mb-10 max-w-5xl mx-auto`}
                        dangerouslySetInnerHTML={{ __html: c.hero_main_title }} />
                    <p className={`text-xl leading-relaxed max-w-2xl mx-auto mb-14 ${muted}`}>{c.hero_description}</p>
                    <div className="flex gap-4 flex-wrap justify-center">
                        {c.hero_btn_1_url ? (
                            <a href={c.hero_btn_1_url}
                                className="px-12 py-5 rounded-full font-bold text-lg text-white hover:scale-105 transition-all"
                                style={{ background: grad, boxShadow: dark ? '0 20px 60px rgba(139,92,246,0.4)' : '0 20px 60px rgba(99,102,241,0.3)' }}>
                                {c.hero_btn_1_text}
                            </a>
                        ) : (
                            <button onClick={openRegister}
                                className="px-12 py-5 rounded-full font-bold text-lg text-white hover:scale-105 transition-all"
                                style={{ background: grad, boxShadow: dark ? '0 20px 60px rgba(139,92,246,0.4)' : '0 20px 60px rgba(99,102,241,0.3)' }}>
                                {c.hero_btn_1_text}
                            </button>
                        )}
                        <Link href={c.hero_btn_2_url}
                            className={`px-12 py-5 rounded-full font-bold text-lg ${text} hover:scale-105 transition-all border`}
                            style={{ background: bg.glass, backdropFilter: 'blur(20px)', borderColor: bg.border }}>
                            {c.hero_btn_2_text} →
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── Stats ─────────────────────────────────────────── */}
            <section className="py-16">
                <div className="max-w-[900px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="text-center py-8 px-4 rounded-3xl hover:scale-105 transition-all"
                            style={{ background: bg.card, backdropFilter: 'blur(10px)', border: `1px solid ${bg.border}` }}>
                            <div className="text-4xl font-black mb-2 bg-clip-text text-transparent"
                                style={{ backgroundImage: dark ? 'linear-gradient(to right,#60a5fa,#a78bfa,#ff7b00)' : 'linear-gradient(to right,#6366f1,#8b5cf6,#f97316)' }}>
                                {c[`stat_number_${i}`]}
                            </div>
                            <div className={`text-xs uppercase tracking-wider ${muted}`}>{c[`stat_label_${i}`]}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── Intro ─────────────────────────────────────────── */}
            {c.show_intro !== false && (
                <section className="py-24">
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="space-y-8 order-2 lg:order-1">
                            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border text-sm font-bold uppercase tracking-wider"
                                style={{ background: dark ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.12)', borderColor: dark ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.4)', color: dark ? '#60a5fa' : '#4f46e5' }}>
                                {c.intro_badge}
                            </div>
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.intro_title }} />
                            <p className={`text-xl leading-relaxed ${muted}`} dangerouslySetInnerHTML={{ __html: c.intro_desc }} />
                            <div className="flex flex-wrap gap-6">
                                {[c.intro_check_1, c.intro_check_2].filter(Boolean).map((t, i) => (
                                    <div key={i} className={`flex items-center gap-3 ${dark ? 'text-white/80' : 'text-slate-700'}`}>
                                        <span className="material-symbols-outlined text-2xl text-blue-400">check_circle</span>
                                        <span className="font-semibold">{t}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative order-1 lg:order-2">
                            <div className="absolute -inset-4 rounded-3xl blur-3xl" style={{ background: 'linear-gradient(135deg,rgba(99,102,241,0.1),rgba(139,92,246,0.1),rgba(249,115,22,0.1))' }} />
                            <div className="relative rounded-3xl overflow-hidden aspect-video" style={{ border: `1px solid rgba(139,92,246,0.3)` }}>
                                <video className="w-full h-full object-cover" autoPlay loop muted playsInline>
                                    <source src={c.intro_video} type="video/mp4" />
                                </video>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* ── Quick Actions ────────────────────────────────────────── */}
            {c.show_quick_actions !== false && (
                <section className="py-24">
                    <div className="max-w-[1400px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-6 max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border text-sm font-bold uppercase tracking-wider"
                                style={{ background: dark ? 'rgba(96,165,250,0.1)' : 'rgba(99,102,241,0.1)', borderColor: dark ? 'rgba(96,165,250,0.3)' : 'rgba(99,102,241,0.35)', color: dark ? '#60a5fa' : '#4f46e5' }}>
                                {c.quick_actions_badge || 'AI Models'}
                            </div>
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.quick_actions_title || 'Explore AI Capability' }} />
                            <p className={`text-lg ${muted}`}>{c.quick_actions_desc || 'Access all premium generation models and tools instantly from your dashboard.'}</p>
                        </div>
                        <HomeQuickActions />
                    </div>
                </section>
            )}

            {/* ── Image Trends ──────────────────────────────────────── */}
            {c.show_trends !== false && (
                <section className="py-24">
                    <div className="max-w-[1400px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-6 max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border text-sm font-bold uppercase tracking-wider"
                                style={{ background: dark ? 'rgba(96,165,250,0.1)' : 'rgba(99,102,241,0.1)', borderColor: dark ? 'rgba(96,165,250,0.3)' : 'rgba(99,102,241,0.35)', color: dark ? '#60a5fa' : '#4f46e5' }}>
                                {c.trends_badge || 'Trending'}
                            </div>
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.trends_title || 'Discover <span class="text-primary">AI Image Trends</span>' }} />
                            <p className={`text-lg ${muted}`}>{c.trends_desc || 'Explore our most popular AI templates and creative presets.'}</p>
                        </div>
                        <HomeImageTrends />
                    </div>
                </section>
            )}

            {/* ── Image Studio ──────────────────────────────────── */}
            {c.show_images !== false && (
                <section className="py-24">
                    <div className="max-w-[1200px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-6 max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border text-sm font-bold uppercase tracking-wider"
                                style={{ background: dark ? 'rgba(96,165,250,0.1)' : 'rgba(99,102,241,0.1)', borderColor: dark ? 'rgba(96,165,250,0.3)' : 'rgba(99,102,241,0.35)', color: dark ? '#60a5fa' : '#4f46e5' }}>
                                {c.image_studio_badge}
                            </div>
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.image_studio_title }} />
                            <p className={`text-lg ${muted}`}>{c.image_studio_desc}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {c.image_cards.map((item, i) => (
                                <div key={i} className={`aspect-[3/4] rounded-3xl overflow-hidden group relative cursor-pointer transition-all duration-300 hover:scale-[1.02] bg-gradient-to-br ${dark ? GRADIENTS_DARK[i] : GRADIENTS_LIGHT[i]} border`}
                                    style={{ backdropFilter: 'blur(20px)', borderColor: bg.border }}>
                                    <div className="w-full h-full bg-cover bg-center group-hover:scale-110 transition-transform duration-700" style={{ backgroundImage: `url('${item.main}')` }} />
                                    <div className="absolute inset-0 bg-black/40 group-hover:bg-transparent transition-colors duration-500 flex items-center justify-center">
                                        <h3 className="text-xl font-bold text-white uppercase tracking-widest group-hover:opacity-0 transition-opacity drop-shadow-xl">{item.title}</h3>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Video Trends ──────────────────────────────────── */}
            {c.show_video_trends !== false && (
                <section className="py-24">
                    <div className="max-w-[1400px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-6 max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border text-sm font-bold uppercase tracking-wider"
                                style={{ background: dark ? 'rgba(167,139,250,0.1)' : 'rgba(139,92,246,0.1)', borderColor: dark ? 'rgba(167,139,250,0.3)' : 'rgba(139,92,246,0.35)', color: dark ? '#a78bfa' : '#7c3aed' }}>
                                {c.video_trends_badge || 'Video Trends'}
                            </div>
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.video_trends_title || 'Trending <span class="text-primary">Avatar Templates</span>' }} />
                            <p className={`text-lg ${muted}`}>{c.video_trends_desc || 'Preview popular avatar video templates before creating your own.'}</p>
                        </div>
                        <HomeVideoTrends />
                    </div>
                </section>
            )}

            {/* ── Video Studio ──────────────────────────────────── */}
            {c.show_videos !== false && (
                <section className="py-24">
                    <div className="max-w-[1200px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-6 max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border text-sm font-bold uppercase tracking-wider"
                                style={{ background: dark ? 'rgba(167,139,250,0.1)' : 'rgba(139,92,246,0.1)', borderColor: dark ? 'rgba(167,139,250,0.3)' : 'rgba(139,92,246,0.35)', color: dark ? '#a78bfa' : '#7c3aed' }}>
                                {c.video_studio_badge}
                            </div>
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.video_studio_title }} />
                            <p className={`text-lg ${muted}`}>{c.video_studio_desc}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {c.video_cards.map((item, i) => (
                                <VideoCard key={i} item={item} cardClass={`rounded-3xl border-0 bg-gradient-to-br ${dark ? GRADIENTS_DARK[i] : GRADIENTS_LIGHT[i]}`} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Chat ──────────────────────────────────────────── */}
            {c.show_chat !== false && (
                <section className="py-24">
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="space-y-8 order-2 lg:order-1">
                            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border text-sm font-bold uppercase tracking-wider"
                                style={{ background: dark ? 'rgba(52,211,153,0.1)' : 'rgba(16,185,129,0.1)', borderColor: dark ? 'rgba(52,211,153,0.3)' : 'rgba(16,185,129,0.35)', color: dark ? '#34d399' : '#059669' }}>
                                {c.chat_badge}
                            </div>
                            <h2 className={`text-5xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.chat_title }} />
                            <p className={`text-xl leading-relaxed ${muted}`}>{c.chat_desc}</p>
                            <ul className="space-y-3">
                                {c.chat_features.map((f, i) => (
                                    <li key={i} className={`flex items-center gap-3 ${dark ? 'text-white/70' : 'text-slate-700'}`}>
                                        <span className="size-2 rounded-full bg-green-400 shrink-0 animate-pulse" />{f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="relative order-1 lg:order-2">
                            <div className="absolute -inset-4 rounded-3xl blur-3xl" style={{ background: dark ? 'radial-gradient(circle,rgba(52,211,153,0.1),transparent)' : 'radial-gradient(circle,rgba(16,185,129,0.08),transparent)' }} />
                            <ChatDemo panelClass={dark ? 'border-green-900/30 bg-white/5' : 'border-green-200/60 bg-white/80'} />
                        </div>
                    </div>
                </section>
            )}

            {/* ── Voice ─────────────────────────────────────────── */}
            {c.show_voice !== false && (
                <section className="py-24">
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="relative">
                            <div className="absolute -inset-4 rounded-3xl blur-3xl" style={{ background: dark ? 'radial-gradient(circle,rgba(244,114,182,0.1),transparent)' : 'radial-gradient(circle,rgba(236,72,153,0.07),transparent)' }} />
                            <VoiceDemo panelClass={dark ? 'border-pink-900/30 bg-white/5' : 'border-pink-200/60 bg-white/80'} />
                        </div>
                        <div className="space-y-8">
                            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border text-sm font-bold uppercase tracking-wider"
                                style={{ background: dark ? 'rgba(244,114,182,0.1)' : 'rgba(236,72,153,0.1)', borderColor: dark ? 'rgba(244,114,182,0.3)' : 'rgba(236,72,153,0.35)', color: dark ? '#f472b6' : '#db2777' }}>
                                {c.voice_badge}
                            </div>
                            <h2 className={`text-5xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.voice_title }} />
                            <p className={`text-xl leading-relaxed ${muted}`}>{c.voice_desc}</p>
                            <ul className="space-y-3">
                                {c.voice_features.map((f, i) => (
                                    <li key={i} className={`flex items-center gap-3 ${dark ? 'text-white/70' : 'text-slate-700'}`}>
                                        <span className="size-2 rounded-full bg-pink-400 shrink-0" />{f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            )}

            {/* ── CTA ───────────────────────────────────────────── */}
            <section className="py-32 text-center">
                <div className="max-w-[700px] mx-auto px-6">
                    <div className="rounded-3xl p-16 relative overflow-hidden"
                        style={{ background: dark ? 'linear-gradient(135deg,rgba(59,130,246,0.1),rgba(139,92,246,0.1),rgba(255,123,0,0.1))' : 'linear-gradient(135deg,rgba(99,102,241,0.12),rgba(139,92,246,0.12),rgba(249,115,22,0.12))', backdropFilter: 'blur(20px)', border: `1px solid ${bg.border}` }}>
                        <div className="absolute inset-0" style={{ background: dark ? 'radial-gradient(circle at center,rgba(139,92,246,0.15),transparent 70%)' : 'radial-gradient(circle at center,rgba(99,102,241,0.08),transparent 70%)' }} />
                        <div className="relative z-10">
                            <h2 className={`text-5xl font-black ${text} mb-6`}>{c.cta_title} <span className="bg-clip-text text-transparent" style={{ backgroundImage: dark ? 'linear-gradient(to right,#60a5fa,#a78bfa,#ff7b00)' : 'linear-gradient(to right,#6366f1,#8b5cf6,#f97316)' }}>{c.cta_subtitle}</span>?</h2>
                            <p className={`text-lg mb-10 ${muted}`}>{c.cta_desc}</p>
                            <button onClick={openRegister}
                                className="px-14 py-5 rounded-2xl font-black text-lg text-white hover:scale-105 transition-all"
                                style={{ background: grad, boxShadow: dark ? '0 20px 50px rgba(139,92,246,0.4)' : '0 20px 50px rgba(99,102,241,0.3)' }}>
                                {c.cta_btn_primary}
                            </button>
                            <p className={`text-sm mt-6 ${muted}`}>{c.cta_tagline}</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
